<template>
  <div class="w-full h-full flex overflow-hidden relative">
    <div
      class="h-full max-h-full"
      :class="leftClasses"
    >
      <!-- Left -->
      <slot name="left" :state="state" :toggle="toggle" :small="small" />
    </div>
    <div
      :class="[
        'flex-grow flex-shrink w-10',
        rightClass
      ]"
    >
      <!-- Right -->
      <slot name="right" :state="state" :toggle="toggle" :small="small" />

      <transition name="fade">
        <div v-if="small && active" class="absolute inset-0 w-full h-full bg-black/50" @click="toggle" />
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'OSidebar' }
</script>

<script setup lang="ts">
import { useUIStore } from '~/stores/useUIStore'

const uiStore = useUIStore()
const slots = useSlots()

interface Props {
  mainHeader?: string | null
  collapseHeader?: string | null
  toggleIcon?: string
  leftClass?: string
  rightClass?: string
  stored?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  mainHeader: null,
  collapseHeader: null,
  toggleIcon: 'FilterIcon',
  leftClass: 'bg-gray-50 dark:bg-gray-900 border-r dark:border-gray-800 z-80',
  rightClass: 'bg-white dark:bg-gray-900',
  stored: false,
})

const collapse = ref(false)
const show = ref(false)
const width = ref(0)

const _collapse = computed({
  get () {
    return props.stored ? uiStore.sidebar : collapse.value
  },
  set (value: boolean) {
    if (props.stored) {
      uiStore.setSidebar(value)
    } else {
      collapse.value = value
    }
  },
})

const small = computed(() => width.value < 1280)
const active = computed(() => small.value && show.value)

const state = computed(() => ({
  collapse: _collapse.value,
  show: show.value,
  width: width.value,
  small: small.value,
  active: active.value,
}))

const hasLeftSlot = computed(() => !!slots.left)

const leftClasses = computed(() => {
  const classes = [props.leftClass]

  classes.push(_collapse.value ? 'hidden' : 'w-72 xl:w-80')

  if (small.value) {
    classes.push('absolute transform z-50 transition-transform duration-200 shadow md:shadow-none bg-white')
    classes.push(active.value ? 'translate-x-0' : ' -translate-x-2/1')
  }

  if (!hasLeftSlot.value) {
    classes.push('hidden')
  }

  return classes
})

watch(small, (val) => {
  if (val) {
    _collapse.value = false
  }
})

onMounted(() => {
  width.value = document.body.clientWidth

  window.addEventListener('resize', () => {
    width.value = document.body.clientWidth
  })
})

function toggle () {
  if (small.value) {
    show.value = !show.value
  } else {
    _collapse.value = !_collapse.value
  }
}
</script>
